import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  PasswordInput,
  Stack,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconArrowRight } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import { portalPasswordService } from "../../services/portalPassword.service";
import { usePortalAuthStore } from "../../store/portalAuth.store";

interface PortalPasswordLoginFormProps {
  caseId: string | null;
}

interface LoginFormValues {
  email: string;
  password: string;
}

export function PortalPasswordLoginForm({
  caseId,
}: PortalPasswordLoginFormProps) {
  const navigate = useNavigate();
  const login = usePortalAuthStore((state) => state.login);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const resetUrl = useMemo(
    () =>
      caseId
        ? `/portal/reset-password?caseId=${encodeURIComponent(caseId)}`
        : "/portal/reset-password",
    [caseId]
  );
  const form = useForm<LoginFormValues>({
    mode: "uncontrolled",
    initialValues: { email: "", password: "" },
    validateInputOnBlur: true,
    validate: {
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value.trim())
          ? null
          : "Enter a valid email address",
      password: (value) => (value ? null : "Password is required"),
    },
  });

  const handleSubmit = useCallback(
    async (values: LoginFormValues) => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const result = await portalPasswordService.login(
          values.email.trim(),
          values.password
        );
        login(result.sessionToken, result.email, result.practiceNames);
        navigate(caseId ? `/portal/report/${caseId}` : "/portal/dashboard");
      } catch (error) {
        setSubmitError(
          error instanceof Error
            ? error.message
            : "We couldn't sign you in. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [caseId, login, navigate]
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <TextInput
          withAsterisk
          label="Email"
          placeholder="doctor@example.com"
          type="email"
          autoComplete="username"
          key={form.key("email")}
          {...form.getInputProps("email")}
        />
        <PasswordInput
          withAsterisk
          label="Password"
          autoComplete="current-password"
          key={form.key("password")}
          {...form.getInputProps("password")}
        />
        {submitError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {submitError}
          </Alert>
        )}
        <Anchor component={Link} to={resetUrl} size="sm">
          Forgot or need to create your password?
        </Anchor>
        <Button
          fullWidth
          type="submit"
          loading={isSubmitting}
          rightSection={<IconArrowRight size={17} />}
        >
          Sign In
        </Button>
      </Stack>
    </form>
  );
}
